import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import {
  useLazyGetProfileQuery,
  useUpdateProfileMutation,
} from "../../service/auth";
import { VendorStore } from "../../types/General";
import { getFromStorage, STORAGE_KEYS } from "../../helpers";
import { User } from "../../types/User";
import { GridCloseIcon } from "@mui/x-data-grid";
import { UploadMedia } from "../../utils/mediaUpload";
import Loader from "../../constants/Loader";

const StoreBranding: React.FC = () => {
  const storeMottoTemplates = [
    "Crafted with Care",
    "Made in the UAE",
    "Celebrate Local Creativity",
    // Admin can add more here
  ];

  const storeStoryTemplates = [
    "Born from a passion for craftsmanship, our store celebrates local artisans and the beauty of handmade traditions...",
    "Inspired by UAE heritage, our brand blends modern aesthetics with timeless cultural values...",
    "Built by creators for creators, our store is a home for unique products made with soul and purpose...",
  ];
  const [getProfile] = useLazyGetProfileQuery();
  const [updateProfile] = useUpdateProfileMutation();
  const [storeStory, setStoreStory] = useState("");
  const [vendorData, setVendorData] = useState<VendorStore | null>(null);
  const [showStoryEditor, setShowStoryEditor] = useState(false);
  const [banner, setBanner] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const userData = getFromStorage(STORAGE_KEYS.userData);
  const parsedUserData: User | null = userData
    ? (JSON.parse(userData) as User)
    : null;
  const BrandFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      motto: vendorData?.storeMotto || "",
      story: vendorData?.storeStory || "",
    },
    validationSchema: Yup.object({
      motto: Yup.string().required("Store Motto is required"),
      story: Yup.string().required("Store Story is required"),
    }),
    onSubmit: async (values: any) => {
      // BankFormik.setSubmitting(true);

      if (!banner) {
        showError("Please upload a banner image");
        return;
      }

      const body = {
        storeBanner: banner,
        storeMotto: values.motto,
        storeStory: values.story,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          getProfileAPi();
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });
  useEffect(() => {
    getProfileAPi();
  }, []);

  const getProfileAPi = async () => {
    setLoading(true);
    try {
      const response = await getProfile({
        id: parsedUserData?.id,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVendorData(response?.data || null);
        setBanner(response?.data?.storeBanner);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setBanner("");
      return;
    }
    setLoading(true);
    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setBanner(imageUrl?.data || "");
    }
    setLoading(false);
  };
  return (
    <>
      <Loader isLoad={loading} />
      <form className="form" onSubmit={BrandFormik.handleSubmit}>
        <div className="gap_p">
          {/* ------------------- STORE BANNER ------------------- */}
          <div className="control_group wrap_viewDemo_steps">
            <label>Store Banner</label>
            {banner ? (
              <div>
                <div style={{ position: "relative" }}>
                  <figure>
                    <CardMedia
                      style={{
                        height: "150px",
                        borderRadius: "12px",
                        objectFit: "cover",
                      }}
                      component="img"
                      image={banner}
                      alt="photo"
                    />
                  </figure>
                  <GridCloseIcon
                    style={{
                      color: "white",
                      position: "absolute",
                      right: 5,
                      top: 5,
                      backgroundColor: "red",
                      borderRadius: "50%",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setBanner("");
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="upload_box">
                <label
                  className="upload_action"
                  // onClick={() => setOpenModal(true)}
                >
                  <p>
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={(e: any) => {
                        handleProfileUpload(e);
                        e.target.value = null;
                      }}
                    />
                    <img src="/static/images/upload_icon.svg" alt="icon" />
                    <strong>Upload Store Banner</strong>
                  </p>
                </label>
              </div>
            )}
          </div>

          {/* =================== STORE MOTTO =================== */}
          <div className="control_group ai_rec_btn1">
            <label>Store Motto</label>

            {/* Templates */}

            <Select fullWidth value={"default"} className="select_abs">
              <MenuItem disabled value="default">
                Select Motto
              </MenuItem>
              {storeMottoTemplates.map((motto, index) => (
                <MenuItem value="All"> {motto}</MenuItem>
              ))}
            </Select>

            {/* Custom Input (editable after selecting template OR clicking AI) */}
            <TextField
              fullWidth
              placeholder="Enter custom motto"
              id="motto"
              name="motto"
              className="text_field"
              value={BrandFormik.values.motto}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  BrandFormik.handleChange(val);
                }
              }}
              onBlur={BrandFormik.handleBlur}
              helperText={
                BrandFormik.touched.motto &&
                typeof BrandFormik.errors.motto === "string"
                  ? BrandFormik.errors.motto
                  : ""
              }
            />

            {/* AI Button */}
            <Button
              className="btn btn_primary sm"
              sx={{ mt: 2 }}
              onClick={() => {
                // Example AI logic
                // const aiGenerated = "A modern celebration of UAE creativity.";
                // setCustomMotto(aiGenerated);
              }}
            >
              <img
                style={{ filter: "brightness(0) invert(1)" }}
                src="/static/images/ai_icon.svg"
                alt=""
              />
              Generate with AI
            </Button>
          </div>

          {/* =================== STORE STORY =================== */}
          <div className="control_group ai_rec_btn">
            <label>Store Story / About Your Brand</label>

            <Select fullWidth value={"default"} className="select_abs">
              <MenuItem disabled value="default">
                Select Template
              </MenuItem>
              {storeStoryTemplates.map((template, index) => (
                <MenuItem value="All">{template}</MenuItem>
              ))}
            </Select>
            <TextField
              className="mt_20 text_field"
              placeholder="Short Story"
              hiddenLabel
              fullWidth
              multiline
              id="story"
              name="story"
              value={BrandFormik.values.story}
              onChange={(val) => {
                if (val.target.value === " " || val.target.value === ".") {
                } else {
                  BrandFormik.handleChange(val);
                }
              }}
              onBlur={BrandFormik.handleBlur}
              helperText={
                BrandFormik.touched.story &&
                typeof BrandFormik.errors.story === "string"
                  ? BrandFormik.errors.story
                  : ""
              }
            />

            {/* AI Button */}
            <Button
              className="btn btn_primary sm"
              onClick={() => {
                const aiStory =
                  "Our journey began with a mission to empower local creators in the UAE. Every product in our store reflects passion, craftsmanship, and cultural pride...";
                setStoreStory(aiStory);
                setShowStoryEditor(true);
              }}
            >
              <img
                style={{ filter: "brightness(0) invert(1)" }}
                src="/static/images/ai_icon.svg"
                alt=""
              />
              Enhance with AI
            </Button>

            {/* Editable Text Field AFTER template/AI */}
            {showStoryEditor && (
              <TextField
                multiline
                fullWidth
                minRows={5}
                placeholder="Write your store story…"
                value={storeStory}
                onChange={(e) => setStoreStory(e.target.value)}
                sx={{ mt: 2 }}
              />
            )}
          </div>
        </div>
        <div style={{ marginTop: "30px" }} className="btn_flex">
          <Button className="btn btn_primary" type="submit">
            Save
          </Button>
        </div>
      </form>
    </>
  );
};

export default StoreBranding;
